// Audience API service for frontend - Audience Knowledge System

import { API_BASE_URL } from '../config/settings';

// Types matching backend
export type AwarenessLevel = 'unaware' | 'problem_aware' | 'solution_aware' | 'product_aware' | 'most_aware';
export type FunnelStage = 'tofu' | 'mofu' | 'bofu';
export type OutputType = 'reel' | 'youtube' | 'newsletter' | 'linkedin_dm' | 'landing_page' | 'ad_copy' | 'thread';

export interface Product {
  id: string;
  name: string;
  description: string;
  valueProposition: string;
  targetMarket: string;
  createdAt: number;
  updatedAt: number;
}

export interface Audience {
  id: string;
  productId: string;
  name: string;
  description: string;
  pains: string[];
  objectives: string[];
  fears: string[];
  desires: string[];
  demographics?: {
    geo?: string[];
    industry?: string[];
    role?: string[];
    companySize?: string[];
  };
  awarenessLevel: AwarenessLevel;
  funnelStage: FunnelStage;
  messagingAngle: string;
  createdAt: number;
  updatedAt: number;
}

export interface Playbook {
  id: string;
  productId: string;
  audienceId?: string;
  name: string;
  description: string;
  centralMessage: string;
  angles: string[];
  sequence?: string;
  enabledOutputs: OutputType[];
  segmentsEnabled: boolean;
  generatedBy: 'ai' | 'manual' | 'hybrid';
  lastEvolved?: number;
  userValidated: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface PlaybookOutput {
  id: string;
  playbookId: string;
  outputType: OutputType;
  config: {
    duration?: number;
    tone?: string;
    cta?: string;
    format?: string;
  };
  template?: string;
  createdAt: number;
  updatedAt: number;
}

export interface PlaybookSegment {
  id: string;
  playbookId: string;
  name: string;
  description: string;
  criteria: {
    geo?: string[];
    industry?: string[];
    role?: string[];
  };
  messagingAdjustments?: string;
  examples?: string[];
  createdAt: number;
  updatedAt: number;
}

// Request types
export interface ProductRequest {
  name: string;
  description: string;
  valueProposition: string;
  targetMarket: string;
}

export interface AudienceRequest {
  productId: string;
  name: string;
  description: string;
  pains: string[];
  objectives: string[];
  fears: string[];
  desires: string[];
  demographics?: Audience['demographics'];
  awarenessLevel: AwarenessLevel;
  funnelStage: FunnelStage;
  messagingAngle: string;
}

export interface PlaybookGenerateRequest {
  productId: string;
  audienceId?: string;
  resourceIds?: string[];
  userGuidance?: string;
}

export interface PlaybookEvolveRequest {
  reason: 'new_resources' | 'metrics' | 'user_request';
  newResourceIds?: string[];
  metrics?: any;
  userGuidance?: string;
}

class AudienceApi {
  private baseUrl = `${API_BASE_URL}/audience`;

  // ============================================
  // PRODUCTS
  // ============================================

  async listProducts(): Promise<Product[]> {
    const res = await fetch(`${this.baseUrl}/products`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list products');
    }

    return json.data;
  }

  async getProduct(id: string): Promise<Product> {
    const res = await fetch(`${this.baseUrl}/products/${id}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get product');
    }

    return json.data;
  }

  async createProduct(data: ProductRequest): Promise<Product> {
    const res = await fetch(`${this.baseUrl}/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to create product');
    }

    return json.data;
  }

  async updateProduct(id: string, data: Partial<ProductRequest>): Promise<Product> {
    const res = await fetch(`${this.baseUrl}/products/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update product');
    }

    return json.data;
  }

  async deleteProduct(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/products/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete product');
    }
  }

  // ============================================
  // AUDIENCES
  // ============================================

  async listAudiences(productId?: string): Promise<Audience[]> {
    const params = new URLSearchParams();
    if (productId) params.set('productId', productId);

    const url = params.toString() ? `${this.baseUrl}/audiences?${params}` : `${this.baseUrl}/audiences`;
    const res = await fetch(url);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list audiences');
    }

    return json.data;
  }

  async getAudience(id: string): Promise<Audience> {
    const res = await fetch(`${this.baseUrl}/audiences/${id}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get audience');
    }

    return json.data;
  }

  async createAudience(data: AudienceRequest): Promise<Audience> {
    const res = await fetch(`${this.baseUrl}/audiences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to create audience');
    }

    return json.data;
  }

  async updateAudience(id: string, data: Partial<AudienceRequest>): Promise<Audience> {
    const res = await fetch(`${this.baseUrl}/audiences/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update audience');
    }

    return json.data;
  }

  async deleteAudience(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/audiences/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete audience');
    }
  }

  // ============================================
  // PLAYBOOKS
  // ============================================

  async listPlaybooks(filters?: { productId?: string; audienceId?: string }): Promise<Playbook[]> {
    const params = new URLSearchParams();
    if (filters?.productId) params.set('productId', filters.productId);
    if (filters?.audienceId) params.set('audienceId', filters.audienceId);

    const url = params.toString() ? `${this.baseUrl}/playbooks?${params}` : `${this.baseUrl}/playbooks`;
    const res = await fetch(url);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list playbooks');
    }

    return json.data;
  }

  async getPlaybook(id: string): Promise<Playbook> {
    const res = await fetch(`${this.baseUrl}/playbooks/${id}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get playbook');
    }

    return json.data;
  }

  async generatePlaybook(data: PlaybookGenerateRequest): Promise<Playbook> {
    const res = await fetch(`${this.baseUrl}/playbooks/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to generate playbook');
    }

    return json.data;
  }

  async updatePlaybook(id: string, data: Partial<Playbook>): Promise<Playbook> {
    const res = await fetch(`${this.baseUrl}/playbooks/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update playbook');
    }

    return json.data;
  }

  async evolvePlaybook(id: string, data: PlaybookEvolveRequest): Promise<Playbook> {
    const res = await fetch(`${this.baseUrl}/playbooks/${id}/evolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to evolve playbook');
    }

    return json.data;
  }

  async deletePlaybook(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/playbooks/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete playbook');
    }
  }

  // ============================================
  // PLAYBOOK OUTPUTS
  // ============================================

  async listOutputs(playbookId: string): Promise<PlaybookOutput[]> {
    const res = await fetch(`${this.baseUrl}/playbooks/${playbookId}/outputs`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list outputs');
    }

    return json.data;
  }

  async createOutput(playbookId: string, data: { outputType: OutputType; config: any; template?: string }): Promise<PlaybookOutput> {
    const res = await fetch(`${this.baseUrl}/playbooks/${playbookId}/outputs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to create output');
    }

    return json.data;
  }

  async updateOutput(id: string, data: { config?: any; template?: string }): Promise<PlaybookOutput> {
    const res = await fetch(`${this.baseUrl}/outputs/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update output');
    }

    return json.data;
  }

  async deleteOutput(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/outputs/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete output');
    }
  }

  // ============================================
  // PLAYBOOK SEGMENTS
  // ============================================

  async listSegments(playbookId: string): Promise<PlaybookSegment[]> {
    const res = await fetch(`${this.baseUrl}/playbooks/${playbookId}/segments`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list segments');
    }

    return json.data;
  }

  async createSegment(playbookId: string, data: Omit<PlaybookSegment, 'id' | 'playbookId' | 'createdAt' | 'updatedAt'>): Promise<PlaybookSegment> {
    const res = await fetch(`${this.baseUrl}/playbooks/${playbookId}/segments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to create segment');
    }

    return json.data;
  }

  async updateSegment(id: string, data: Partial<PlaybookSegment>): Promise<PlaybookSegment> {
    const res = await fetch(`${this.baseUrl}/segments/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update segment');
    }

    return json.data;
  }

  async deleteSegment(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/segments/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete segment');
    }
  }

  // ============================================
  // CONTENT GENERATION
  // ============================================

  async generateContent(data: {
    playbookId: string;
    outputType: OutputType;
    segmentId?: string;
    provider?: 'claude' | 'poppy' | 'both';
    userGuidance?: string;
  }): Promise<any> {
    const res = await fetch(`${this.baseUrl}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to generate content');
    }

    return json.data;
  }

  async getContentOutput(id: string): Promise<any> {
    const res = await fetch(`${this.baseUrl}/outputs/${id}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get content output');
    }

    return json.data;
  }

  async updateOutputStatus(id: string, status: 'draft' | 'published' | 'archived'): Promise<void> {
    const res = await fetch(`${this.baseUrl}/outputs/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update output status');
    }
  }

  async saveOutputMetrics(id: string, metrics: any): Promise<void> {
    const res = await fetch(`${this.baseUrl}/outputs/${id}/metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(metrics)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to save metrics');
    }
  }

  async listContentOutputs(playbookId: string): Promise<any[]> {
    const res = await fetch(`${this.baseUrl}/playbooks/${playbookId}/content-outputs`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list content outputs');
    }

    return json.data;
  }
}

export const audienceApi = new AudienceApi();
